import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllUsers, checkSession } from "../utils/api";

// ✅ Shared components
import AdminNavbar from "./AdminNavbar";
import Banner from "./ui/Banner";

// ✅ Users subcomponents
import UserTable from "./users/UserTable";
import UserFormModal from "./users/UserFormModal";
import DeleteConfirmModal from "./users/DeleteConfirmModal";
import ResetPasswordModal from "./users/ResetPasswordModal";

function UsersMaintenance() {
  const [users, setUsers] = useState([]);
  const [session, setSession] = useState(null);

  // ✅ Banner state
  const [banner, setBanner] = useState({ type: "", message: "" });

  // ✅ Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // ✅ Reset password modal state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetId, setResetId] = useState(null);

  // ✅ Form + IDs
  const [form, setForm] = useState({
    name: "",
    username: "",
    email: "",
    role: "user",
    password: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const navigate = useNavigate();

  // ✅ Refresh helper with banner feedback
  const refresh = async () => {
    const res = await getAllUsers();
    if (res.success) {
      setUsers(res.users);
    } else {
      setBanner({ type: "danger", message: res.message });
      setUsers([]);
    }
  };

  // ✅ Session check
  useEffect(() => {
    checkSession()
      .then((res) => {
        if (!res.loggedIn) {
          navigate("/login");
        } else {
          setSession(res);
          refresh();
        }
      })
      .catch((err) => {
        console.error("Session check failed:", err);
        navigate("/login");
      });
  }, [navigate]);

  return (
    <div>
      <AdminNavbar onLogout={() => navigate("/")} />

      <div className="container mt-5 pt-4">
        <h2>👥 Users Maintenance</h2>

        {/* ✅ Banner feedback */}
        <Banner
          type={banner.type}
          message={banner.message}
          onClose={() => setBanner({ type: "", message: "" })}
        />

        {/* ✅ Add user button */}
        <button
          className="btn btn-primary mb-3"
          onClick={() => {
            setForm({
              name: "",
              username: "",
              email: "",
              role: "user",
              password: "",
            });
            setShowCreateModal(true);
          }}
        >
          Add User
        </button>

        {/* ✅ Users table */}
        <UserTable
          users={users}
          session={session}
          onEdit={(u) => {
            setEditingId(u.id);
            setForm({
              name: u.name || "",
              username: u.username || "",
              email: u.email || "",
              role: u.role || "user",
              password: "", // leave blank for update
            });
            setShowUpdateModal(true);
          }}
          onDelete={(id) => {
            setDeleteId(id);
            setShowDeleteModal(true);
          }}
          onReset={(id) => {
            setResetId(id);
            setShowResetModal(true);
          }}
        />

        {/* ✅ Modals */}
        <UserFormModal
          show={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          mode="create"
          form={form}
          setForm={setForm}
          refresh={refresh}
          setBanner={setBanner}
        />

        <UserFormModal
          show={showUpdateModal}
          onClose={() => setShowUpdateModal(false)}
          mode="update"
          form={form}
          setForm={setForm}
          editingId={editingId}
          refresh={refresh}
          setBanner={setBanner}
        />

        <DeleteConfirmModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          userId={deleteId}
          refresh={refresh}
          setBanner={setBanner}
        />

        <ResetPasswordModal
          show={showResetModal}
          onClose={() => setShowResetModal(false)}
          userId={resetId}
          refresh={refresh}
          setBanner={setBanner}
        />
      </div>
    </div>
  );
}

export default UsersMaintenance;
